import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatChip, MatChipSet } from '@angular/material/chips'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderBasicPaneLayoutComponent,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderThemeDirective,
  ElderToastService,
  ElderValidationErrorDirective,
  Unit,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../features/validators/id-validator'
import { Property, PropertyType } from '../property'
import { PropertyKindSelectDirective } from '../property-kind-select/property-kind-select.directive'
import { PropertyService } from '../property.service'

@Component({
  selector: 'app-property-detail',
  templateUrl: './property-detail.component.html',
  styleUrls: ['./property-detail.component.scss'],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneContentComponent,
    ElderPaneActionsComponent,
    ElderPaneSubtitleComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ElderValidationErrorDirective,
    ElderSelectComponent,
    PropertyKindSelectDirective,
    MatChipSet,
    MatChip,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class PropertyDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _property$: BehaviorSubject<Property> = new BehaviorSubject(null)

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  // Enums
  public propertyType = PropertyType
  public propertyTypeList = (Object as any).values(PropertyType)
  public unitList = (Object as any).values(Unit)

  public readonly idValidatorPattern = IdValidator.pattern

  private propertyService = inject(PropertyService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    this.property = route.snapshot.data['property']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.property) {
      this.property = this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get property$(): Observable<Property> {
    return this._property$.asObservable()
  }

  public get property(): Property {
    return this._property$.getValue()
  }

  @Input()
  public set property(prodProperty: Property) {
    if (prodProperty) {
      this._creationMode = !prodProperty.id

      this._property$.next(prodProperty)
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public propertyHasUnitInformation(type: PropertyType): boolean {
    switch (type) {
      case PropertyType.QUANTITY:
        return true
      case PropertyType.QUANTITY_LIST:
        return true
      case PropertyType.QUANTITY_ENUMERATION:
        return true
      default:
        return false
    }
  }

  public reevaluateUnit(type: PropertyType): void {
    if (!this.propertyHasUnitInformation(type)) {
      this.property.unit = null
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.property.id) {
      this.propertyService.findById(this.property.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    this.propertyService.update(this.property).subscribe({
      next: (updated) => {
        this.property = updated
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.toastService.info('Deletion not implemented!')
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): Property {
    return new Property()
  }
}
